import { inject, Injectable } from '@angular/core';
import {
  IconService,
  ResourceService,
  SelectableTableSetupService,
  SetupService,
  TitleService,
  UserService,
} from '@DataFriends-Group/ngx-core';
import { finalize, map, Observable, zip } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StartUpService {
  loading = false;

  private titleSvc = inject(TitleService);
  private setupSvc = inject(SetupService);
  private resourceSvc = inject(ResourceService);
  private iconSvc = inject(IconService);
  private personSvc = inject(SelectableTableSetupService);
  private userSvc = inject(UserService);

  /**
   * Main startup method to set all required configuration
   * @returns {Observable<void>}
   */
  startUp(): Observable<void> {
    this.commonSetup();
    this.userSetup();
    return this.withLoading(zip([this.iconsRegistrations(), this.translations()])).pipe(
      map(() => void 0),
    );
  }

  /**
   * Wraps the given Observable with loading indicator functionality.
   *
   * @param {Observable<unknown>} observable - The Observable to wrap.
   * @return {Observable<unknown>} - The wrapped Observable with loading indicator functionality.
   * @private
   */
  private withLoading(observable: Observable<unknown>): Observable<unknown> {
    this.loading = true;
    return observable.pipe(finalize(() => (this.loading = false)));
  }

  /**
   * Load user specific resources
   */
  private userSetup(): void {
    this.personSvc.load();
  }

  /**
   * Load resources from i18n be controller.
   * @private
   */
  private translations(): Observable<void> {
    return this.resourceSvc.loadResources(['i18n/cs-CZ.json']);
  }

  /**
   * Registers a set of icons using the icon service and ensures
   * shared icons are registered as well.
   *
   * @return {Observable<void>} An observable indicating the
   * completion of the shared icon registration process.
   */
  private iconsRegistrations(): Observable<void> {
    this.registerIcon('gavel');
    this.registerIcon('archive');
    this.registerIcon('building-skyscraper');
    this.registerIcon('affiliate');
    this.registerIcon('versions');
    this.registerIcon('arrow-big-up-lines');
    this.registerIcon('file-download');
    this.registerIcon('file');
    this.registerIcon('file-broken');
    this.registerIcon('report-search');
    this.registerIcon('external-link');
    this.registerIcon('upload');
    return this.iconSvc.registerShared();
  }

  /**
   * Registers an icon with the icon service.
   *
   * @param {string} icon - The name of the icon to register.
   * @return {void} This method does not return a value.
   */
  private registerIcon(icon: string): void {
    this.iconSvc.register(icon, `icon/${icon}.svg`);
  }

  /**
   * Common setup
   * @private
   */
  private commonSetup(): void {
    this.titleSvc.host = 'GrantPilot';
    this.setupSvc.formInputAppearance = 'outline';
    this.setupSvc.colors = {
      primary: '#2ec4b6',
      accent: '#76f37e',
      warn: '#f44336',
      disabled: '#F4F4F4',
      silent: '#d7e1d6',
      font: '',
    };
    this.userSvc.rolesEnabled = true;
  }
}
